import type { UsExamType } from "$global-providers/rxdb-provider/collections/epocus-exam-worksheets";
import type {
  WorksheetFieldDef,
  WorksheetOption,
} from "../worksheet-field-defs";

export type UsShowWhen = { field: string; in: readonly string[] };

export type UsOption = WorksheetOption & {
  reveals?: readonly UsFieldDef[];
  sided?: boolean;
};

export type UsFieldDef = (
  | Exclude<WorksheetFieldDef<UsShowWhen, UsOption>, { kind: "multi" }>
  | (Extract<WorksheetFieldDef<UsShowWhen, UsOption>, { kind: "multi" }> & {
      optional?: boolean;
    })
  | {
      kind: "dropdown";
      id: string;
      label: string;
      options: readonly UsOption[];
      normal: string | null;
      showWhen?: UsShowWhen;
    }
) & { heading?: string };

export type UsSectionSide = "right" | "left" | "right_eye" | "left_eye";

export type UsSection = {
  id: string;
  label: string;
  role: "findings" | "impression";
  technicallyLimited?: boolean;
  technicallyLimitedBy?: string;
  technicallyLimitedReason?: string;
  beforeIndication?: boolean;
  repeatPerSide?: readonly UsSectionSide[];
  showSideWhen?: {
    field: string;
    in: Partial<Record<UsSectionSide, readonly string[]>>;
  };
  fields: readonly UsFieldDef[];
};

export type UsBillingCode = { cpt: string; label: string; preTick?: boolean };

export type UsWorksheetDefinition = {
  examType: UsExamType;
  version: number;
  indications: readonly UsOption[];
  otherIndication?: boolean;
  fixedIndication?: boolean;
  views?: readonly WorksheetOption[];
  sections: readonly UsSection[];
  normalImpressions?: readonly { sectionId: string; value: string }[];
  billing: { codes: readonly UsBillingCode[] };
};

type FieldExtras = { normal?: string; showWhen?: UsShowWhen; heading?: string };

export const option = (
  value: string,
  label: string,
  extras: Omit<UsOption, "value" | "label"> = {},
): UsOption => ({ value, label, ...extras });

export const OTHER = option("other", "Other", { revealsText: true });

export const single = (
  id: string,
  label: string,
  options: readonly UsOption[],
  { normal, showWhen, heading }: FieldExtras = {},
): UsFieldDef => ({
  kind: "single",
  id,
  label,
  options,
  normal: normal ?? null,
  showWhen,
  heading,
});

export const dropdown = (
  id: string,
  label: string,
  options: readonly UsOption[],
  { normal, showWhen, heading }: FieldExtras = {},
): UsFieldDef => ({
  kind: "dropdown",
  id,
  label,
  options,
  normal: normal ?? null,
  showWhen,
  heading,
});

export const multi = (
  id: string,
  label: string,
  options: readonly UsOption[],
  { showWhen, optional }: { showWhen?: UsShowWhen; optional?: boolean } = {},
): UsFieldDef => ({ kind: "multi", id, label, options, showWhen, optional });

export const number = (
  id: string,
  label: string,
  unit: string,
  showWhen?: UsShowWhen,
): UsFieldDef => ({ kind: "number", id, label, unit, showWhen });

export const text = (
  id: string,
  label: string,
  showWhen?: UsShowWhen,
): UsFieldDef => ({ kind: "text", id, label, showWhen });

export const sidedKey = (id: string, side: UsSectionSide): string => {
  const dot = id.indexOf(".");
  if (dot < 0) throw new Error(`Field id ${id} has no group namespace`);
  return `${id.slice(0, dot)}.${side}${id.slice(dot)}`;
};
